import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { X } from "lucide-react";
import axios from "axios";
import NavigationBar from "../layout/NavigationBar";
import Footer from "../layout/footer";
import { FaArrowLeft } from "react-icons/fa";

function AddGift() {
  const [searchParams] = useSearchParams();
  const giftId = searchParams.get("id");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSubCategories, setSelectedSubCategories] = useState([]);
  const [subCategoryData, setSubCategoryData] = useState({});
  const [logo, setLogo] = useState(null);
  const [price, setPrice] = useState("");
  const [fullategory_id, setCategory_id] = useState("");
  const [uploadedLogoUrl, setUploadedLogoUrl] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [titleError, setTitleError] = useState("");
  const [categoryError, setCategoryError] = useState("");
  const [subCategoryError, setSubCategoryError] = useState("");
  const [logoError, setLogoError] = useState("");
  const [isFormProcessing, setIsFormProcessing] = useState(false);
  const [categoryData, setCategoryData] = useState([]);
  const [quantity, setQuantity] = useState("");
  const [images, setImages] = useState([]); // Array of image files
  const [uploadedImageUrls, setUploadedImageUrls] = useState([]);

  const navigate = useNavigate();

  // Fetch initial category data
  const categoryDataValues = () => {
    axios
      .get(`${process.env.REACT_APP_APIURL}category/list?front_category=true`)
      .then((res) => {
        if (res.data.status === "success") {
          const data = res.data.result.map((item) => item.category);
          setCategoryData(data);
        } else {
          console.log("Failed to fetch data.");
        }
      })
      .catch((error) => {
        console.error("Error fetching category data:", error);
      });
  };

  useEffect(() => {
    categoryDataValues();
  }, []);

  useEffect(() => {
    if (giftId) {
      fetchGiftData(giftId);
    }
  }, [giftId]);
  const handleMultipleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const maxImages = 5; // Limit to 5 images

    if (files.length + images.length + uploadedImageUrls.length > maxImages) {
      setErrorMessage(`You can only upload up to ${maxImages} images`);
      return;
    }

    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = function (event) {
        const img = new Image();
        img.src = event.target.result;

        img.onload = function () {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          const maxWidth = 800;
          const maxHeight = 600;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > maxWidth) {
              height *= maxWidth / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width *= maxHeight / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);

          const resizedDataURL = canvas.toDataURL("image/jpeg");
          const blob = dataURItoBlob(resizedDataURL, file.name);

          setImages(prev => [...prev, blob]);
        };
      };
      reader.readAsDataURL(file);
    });
  };

  // Remove image function
  const handleRemoveImage = (index, type) => {
    if (type === 'new') {
      setImages(prev => prev.filter((_, i) => i !== index));
    } else {
      setUploadedImageUrls(prev => prev.filter((_, i) => i !== index));
    }
  };

  const fetchGiftData = (id) => {
    axios
      .get(`${process.env.REACT_APP_APIURL}gift/list`, {
        params: {
          gift_id: id,
          admin: true,
        },
      })
      .then((res) => {
        if (res.data.status === "success") {
          const giftdata = res.data.result;
          setTitle(giftdata.name);
          setPrice(giftdata.price);
          setQuantity(giftdata.quantity);

          // Handle multiple images
          if (giftdata.images && giftdata.images.length > 0) {
            setUploadedImageUrls(giftdata.images);
          } else if (giftdata.url) {
            // Backward compatibility - convert single URL to array
            setUploadedImageUrls([giftdata.url]);
          }

          setSelectedSubCategories(giftdata.category_id);
          if (Array.isArray(giftdata.category_id)) {
            const categories = [
              ...new Set(
                giftdata.category_id.map(
                  (cat) => cat.category || cat.parentCategory
                )
              ),
            ];
            setSelectedCategories(categories);

            setSelectedSubCategories(
              giftdata.category_id.map((cat) => ({
                id: cat._id || cat.id,
                subcategory: cat.subcategory || cat.name,
                parentCategory: cat.category || cat.parentCategory,
                _id: cat._id,
              }))
            );

            categories.forEach((category) => {
              fetchSubCategories(category);
            });
          }
        }
      })
      .catch((err) => {
        console.error("Error fetching gift data:", err);
        setErrorMessage("Failed to fetch gift data");
      });
  };

  // Handle category selection
  const handleCategorySelect = (category) => {
    if (!selectedCategories.includes(category)) {
      const updatedCategories = [...selectedCategories, category];
      setSelectedCategories(updatedCategories);
      fetchSubCategories(category);
    }
  };

  // Handle category removal
  // const handleRemoveCategory = (categoryToRemove) => {
  //   setSelectedCategories(selectedCategories.filter(cat => cat !== categoryToRemove));
  //   // Remove associated subcategories
  //   setSelectedSubCategories(selectedSubCategories.filter(subCat =>
  //     subCategoryData[categoryToRemove]?.findIndex(sc => sc.id === subCat.id) === -1
  //   ));
  // };
  const handleRemoveCategory = (categoryToRemove) => {
    setSelectedCategories(
      selectedCategories.filter((cat) => cat !== categoryToRemove)
    );
    // Remove all subcategories belonging to this category
    setSelectedSubCategories(
      selectedSubCategories.filter(
        (subCat) => subCat.parentCategory !== categoryToRemove
      )
    );
  };

  // Fetch subcategories for a category
  const fetchSubCategories = (category) => {
    const apiUrl = `${process.env.REACT_APP_APIURL}category/list?category=${category}`;
    axios
      .get(apiUrl)
      .then((res) => {
        const data = res.data.result.map((item) => ({
          id: item._id,
          subcategory: item.subcategory,
          parentCategory: category,
        }));
        setSubCategoryData((prev) => ({
          ...prev,
          [category]: data,
        }));
      })
      .catch((err) => {
        console.error("Error fetching subcategories:", err);
      });
  };
  const handleSubCategorySelect = (subcategory) => {
    if (!selectedSubCategories.find((sc) => sc.id === subcategory.id)) {
      setSelectedSubCategories([...selectedSubCategories, subcategory]);
    }
  };
  // const handleRemoveSubCategory = (subcategoryId) => {
  //   console.log("----------subcategoryId--------------")
  //   console.log(subcategoryId)
  //   setSelectedSubCategories(selectedSubCategories.filter(sc => sc.id !== subcategoryId));
  // };
  const handleRemoveSubCategory = (subcategoryId) => {
    setSelectedSubCategories(
      selectedSubCategories.filter(
        (sc) => sc.id !== subcategoryId && sc._id !== subcategoryId
      )
    );
  };

  // Form submission handler
  const handleAddGift = async (e) => {
    e.preventDefault();
    setIsFormProcessing(true);

    let valid = true;

    if (title.trim() === "") {
      setTitleError("Title is required");
      valid = false;
    }

    if (selectedSubCategories.length === 0) {
      setSubCategoryError("At least one subcategory is required");
      valid = false;
    }

    if (images.length === 0 && uploadedImageUrls.length === 0) {
      setLogoError("At least one image is required");
      valid = false;
    }

    if (!valid) {
      setIsFormProcessing(false);
      return;
    }

    try {
      let allImageUrls = [...uploadedImageUrls];

      if (images.length > 0) {
        const newImageUrls = await fileUpload();
        allImageUrls = [...allImageUrls, ...newImageUrls];
      }

      const apiUrl = giftId
        ? `${process.env.REACT_APP_APIURL}gift/update`
        : `${process.env.REACT_APP_APIURL}gift/create`;

      const payload = {
        name: title,
        description: description,
        url: allImageUrls[0], // Main image for backward compatibility
        images: allImageUrls.join(','), // Send as comma-separated string
        price: price,
        quantity: quantity,
        gift_id: giftId || "",
        categories: selectedCategories,
        category_id: selectedSubCategories
          .flatMap((subCat) => [subCat._id, subCat.id])
          .filter(Boolean)
          .filter((id) => id.trim() !== "")
          .join(","),
        subcategories: selectedSubCategories.map((sc) => ({
          id: sc.id,
          parentCategory: sc.parentCategory,
        })),
      };

      const businessId = localStorage.getItem("business_id");
      const signupStatus = localStorage.getItem("signup");

      if (businessId && signupStatus === "user") {
        payload.business_id = businessId;
      }

      const response = await axios.post(apiUrl, payload);

      if (response.data.status === "success") {
        setSuccessMessage(
          giftId ? "Gift updated successfully!" : "Gift added successfully!"
        );
        setTimeout(() => {
          navigate("/gift");
        }, 2000);
      } else {
        setErrorMessage("Failed to add/update gift");
      }
    } catch (err) {
      console.error("Error:", err);
      setErrorMessage("Failed to process request");
    } finally {
      setIsFormProcessing(false);
    }
  };

  // Add this function to handle file uploads
  const fileUpload = async () => {
    try {
      const uploadPromises = images.map(async (image) => {
        const formData = new FormData();
        formData.append("file", image);

        const res = await axios.post(`${process.env.REACT_APP_APIURL}file/upload`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        if (res.data.status === "success") {
          return res.data.result.files[0].file_url;
        } else {
          throw new Error("File upload failed");
        }
      });

      const uploadedUrls = await Promise.all(uploadPromises);
      return uploadedUrls;
    } catch (error) {
      throw new Error("Failed to upload images");
    }
  };


  // Add this function to handle file input changes
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    const reader = new FileReader();

    reader.onload = function (event) {
      const img = new Image();
      img.src = event.target.result;

      img.onload = function () {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        // Set the desired width and height for the resized image
        const maxWidth = 800;
        const maxHeight = 600;

        let width = img.width;
        let height = img.height;

        // Calculate the new dimensions while maintaining the aspect ratio
        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;

        // Draw the image onto the canvas with the new dimensions
        ctx.drawImage(img, 0, 0, width, height);

        // Convert the canvas content back to a data URL
        const resizedDataURL = canvas.toDataURL("image/jpeg");

        // Create a Blob object from the data URL with specified filename
        const blob = dataURItoBlob(resizedDataURL, file.name);

        // Set the resized image as the new logo
        setLogo(blob);
      };
    };

    reader.readAsDataURL(file);
  };

  // Function to convert data URI to Blob with specified filename
  const dataURItoBlob = (dataURI, filename) => {
    const byteString = atob(dataURI.split(",")[1]);
    const mimeString = dataURI.split(",")[0].split(":")[1].split(";")[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    // Create a new Blob with the specified filename
    return new File([ab], filename, { type: mimeString });
  };

  console.log("-----------selectedSubCategories-------------------");
  console.log(selectedSubCategories);

  return (
    <div className="wrapper">
      <header>
        <NavigationBar />
      </header>

      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6 d-flex align-items-center">
                  <FaArrowLeft
                style={{ cursor: "pointer", marginRight: "10px" }}
                onClick={() => navigate("/gift")}
                size={20}
              />
                <h1 className="m-0 txt_lft">
                  {giftId ? "Edit Product" : "Add Product"}
                </h1>
              </div>
            </div>
          </div>
        </div>

        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <form className="form-horizontal" onSubmit={handleAddGift}>
                      <div className="card-body">
                        {/* Title Field */}
                        <div className="form-group row">
                          <label className="col-12 col-md-4 col-form-label">
                            Title<span className="text-danger">*</span>
                          </label>
                          <div className="col-12 col-md-8">
                            <input
                              type="text"
                              className="form-control"
                              value={title}
                              onChange={(e) => setTitle(e.target.value)}
                            />
                            {titleError && (
                              <small className="text-danger">
                                {titleError}
                              </small>
                            )}
                          </div>
                        </div>

                        {/* Categories Selection */}
                        <div className="form-group row">
                          <label className="col-12 col-md-4 col-form-label">
                            Categories<span className="text-danger">*</span>
                          </label>
                          <div className="col-12 col-md-8">
                            <select
                              className="form-control mb-2"
                              onChange={(e) =>
                                handleCategorySelect(e.target.value)
                              }
                              value=""
                            >
                              <option value="">Select categories</option>
                              {categoryData
                                .filter(
                                  (cat) => !selectedCategories.includes(cat)
                                )
                                .map((category, index) => (
                                  <option key={index} value={category}>
                                    {category}
                                  </option>
                                ))}
                            </select>

                            {/* Selected Categories Display */}
                            <div className="selected-items mt-2">
                              {selectedCategories.map((category, index) => (
                                <div
                                  key={index}
                                  className="badge bg-primary p-2 m-1"
                                >
                                  {category}
                                  <button
                                    type="button"
                                    className="btn btn-link text-white p-0 ml-2"
                                    onClick={() =>
                                      handleRemoveCategory(category)
                                    }
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                              ))}
                            </div>
                            {categoryError && (
                              <small className="text-danger">
                                {categoryError}
                              </small>
                            )}
                          </div>
                        </div>
                        <div className="form-group row">
                          <label className="col-12 col-md-4 col-form-label">
                            SubCategories<span className="text-danger">*</span>
                          </label>
                          <div className="col-12 col-md-8">
                            {selectedCategories.map((category, index) => (
                              <div key={index} className="mb-2">
                                <label className="text-muted mb-1">
                                  {category}
                                </label>
                                <select
                                  className="form-control"
                                  onChange={(e) => {
                                    const selected = subCategoryData[
                                      category
                                    ].find((sc) => sc.id === e.target.value);
                                    if (selected) {
                                      handleSubCategorySelect({
                                        ...selected,
                                        parentCategory: category,
                                      });
                                    }
                                  }}
                                  value=""
                                >
                                  <option value="">
                                    Select subcategories for {category}
                                  </option>
                                  {subCategoryData[category]?.map(
                                    (subCat, idx) => (
                                      <option key={idx} value={subCat.id}>
                                        {subCat.subcategory}
                                      </option>
                                    )
                                  )}
                                </select>
                              </div>
                            ))}
                            {/* Selected Subcategories Display */}
                            <div className="selected-items mt-2">
                              {selectedSubCategories.map((subCategory) => (
                                <div
                                  key={subCategory.id || subCategory._id}
                                  className="badge bg-info p-2 m-1"
                                >
                                  {subCategory.subcategory}
                                  <button
                                    type="button"
                                    className="btn btn-link text-white p-0 ml-2"
                                    onClick={() =>
                                      handleRemoveSubCategory(
                                        subCategory.id || subCategory._id
                                      )
                                    }
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                              ))}
                            </div>

                            {subCategoryError && (
                              <small className="text-danger">
                                {subCategoryError}
                              </small>
                            )}
                          </div>
                        </div>

                        {/* Image Upload */}
                        <div className="form-group row">
                          <label className="col-12 col-md-4 col-form-label">
                            Images<span className="text-danger">*</span> (Max 5)
                          </label>
                          <div className="col-12 col-md-8">
                            <div className="position-relative">
                              <label className="btn-bs-file btn btn-primary">
                                Browse Images
                                <input
                                  type="file"
                                  multiple
                                  accept="image/*"
                                  onChange={handleMultipleFileChange}
                                />
                              </label>
                              <small className="text-muted ml-2">Select up to 5 images</small>
                            </div>

                            {/* Display uploaded images */}
                            <div className="images-grid mt-3">
                              {uploadedImageUrls.length > 0 && (
                                <div className="row">
                                  <div className="col-12">
                                    <h6>Current Images:</h6>
                                    <div className="d-flex flex-wrap">
                                      {uploadedImageUrls.map((imageUrl, index) => (
                                        <div key={`uploaded-${index}`} className="product_imgdiv mr-2 mb-2" style={{ width: '120px', height: '120px' }}>
                                          <img
                                            src={imageUrl}
                                            alt={`Product ${index + 1}`}
                                            className="img-fluid"
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                          />
                                          <div className="product_imgdel">
                                            <X
                                              className="cursor-pointer"
                                              onClick={() => handleRemoveImage(index, 'uploaded')}
                                            />
                                          </div>
                                          {index === 0 && <small className="badge badge-primary">Main</small>}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Display new images to be uploaded */}
                              {images.length > 0 && (
                                <div className="row mt-2">
                                  <div className="col-12">
                                    <h6>New Images to Upload:</h6>
                                    <div className="d-flex flex-wrap">
                                      {images.map((image, index) => (
                                        <div key={`new-${index}`} className="product_imgdiv mr-2 mb-2" style={{ width: '120px', height: '120px' }}>
                                          <img
                                            src={URL.createObjectURL(image)}
                                            alt={`New ${index + 1}`}
                                            className="img-fluid"
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                          />
                                          <div className="product_imgdel">
                                            <X
                                              className="cursor-pointer"
                                              onClick={() => handleRemoveImage(index, 'new')}
                                            />
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>

                            {logoError && (
                              <small className="text-danger">{logoError}</small>
                            )}
                          </div>
                        </div>

                        {/* Price Field */}
                        <div className="form-group row">
                          <label className="col-12 col-md-4 col-form-label">
                            Price ₹<span className="text-danger">*</span>
                          </label>
                          <div className="col-12 col-md-8">
                            <input
                              type="number"
                              className="form-control"
                              value={price}
                              onChange={(e) => setPrice(e.target.value)}
                              min="0"
                              step="0.01"
                            />
                          </div>
                        </div>
                        <div className="form-group row">
                          <label className="col-12 col-md-4 col-form-label">
                            Quantity<span className="text-danger">*</span>
                          </label>
                          <div className="col-12 col-md-8">
                            <input
                              type="number"
                              className="form-control"
                              value={quantity}
                              onChange={(e) => setQuantity(e.target.value)}
                              min="0"
                            />
                          </div>
                        </div>

                        {/* Messages */}
                        {errorMessage && (
                          <div className="alert alert-danger" role="alert">
                            {errorMessage}
                          </div>
                        )}
                        {successMessage && (
                          <div className="alert alert-success" role="alert">
                            {successMessage}
                          </div>
                        )}
                      </div>

                      {/* Form Submit */}
                      <div className="card-footer text-center">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={isFormProcessing}
                        >
                          {isFormProcessing
                            ? "Processing..."
                            : giftId
                              ? "Update"
                              : "Add"}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default AddGift;
